import React, { useState } from 'react';
import moment from 'moment-timezone';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { observer } from 'mobx-react';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import ColumnSelector from 'ats/components/column-selector/ColumnSelector';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { eventManager } from 'ats/utils/EventManager';
import UrgencyLabel from '../pipeline/components/UrgencyLabel';
import BulkActionsBar from 'ats/components/bulk-actions-bar/BulkActionsBar';
import AtsTooltip from 'ats/components/ats-tooltip/AtsTooltip';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';

function JobPositionsTable({
	data,
	onClickRow,
	activeRowId,
	sort,
	onSort,
	onSuccess,
	visibleColumns,
	onVisibleColumnsChange,
	isEmpty,
	emptyState,
	loading
}) {
	const [selectedIds, setSelectedIds] = useState([]);

	const getUrgency = value =>
		jobPositionStore.urgencyOptions.find(urgency => urgency.value === value);

	const getStatus = value =>
		jobPositionStore.statusOptions.find(status => status.value === value);

	const getPriority = value =>
		jobPositionStore.priorityOptions.find(priority => priority.value === value);

	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;
		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const shouldShowColumn = column =>
		visibleColumns.some(visibleColumn => visibleColumn.value === column);

	const handleSelectAll = e => {
		if (e.target.checked) {
			setSelectedIds(data.map(row => row.id));
		} else {
			setSelectedIds([]);
		}
	};

	const handleSelectRow = (e, rowId) => {
		e.stopPropagation();
		if (e.target.checked) {
			setSelectedIds(prev => [...prev, rowId]);
		} else {
			setSelectedIds(prev => prev.filter(id => id !== rowId));
		}
	};

	const isAllSelected = data.length > 0 && selectedIds.length === data.length;
	const isPartiallySelected =
		selectedIds.length > 0 && selectedIds.length < data.length;

	const handleBulkChangePriority = async priority => {
		try {
			const result = await jobPositionStore.bulkUpdate(selectedIds, {
				priority: priority.value
			});
			if (result.status === 'success') {
				notificationStore.pushSuccessNotification(
					'Priority Updated',
					`Successfully updated ${result.data.updated} job(s) to priority ${priority.text}`
				);
				selectedIds.forEach(id => {
					eventManager.emit('jobPositionChanged', { jobPositionId: id });
				});
				onSuccess?.();
			} else {
				notificationStore.pushErrorNotification(
					'Update Failed',
					'Failed to update jobs'
				);
			}
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update some jobs'
			);
		}
	};

	const handleBulkChangeUrgency = async urgency => {
		try {
			const result = await jobPositionStore.bulkUpdate(selectedIds, {
				urgency: urgency.value
			});
			if (result.status === 'success') {
				notificationStore.pushSuccessNotification(
					'Urgency Updated',
					`Successfully updated ${result.data.updated} job(s) to ${urgency.text}`
				);
				selectedIds.forEach(id => {
					eventManager.emit('jobPositionChanged', { jobPositionId: id });
				});
				onSuccess?.();
			} else {
				notificationStore.pushErrorNotification(
					'Update Failed',
					'Failed to update jobs'
				);
			}
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update some jobs'
			);
		}
	};

	const handleBulkChangeStatus = async status => {
		try {
			const result = await jobPositionStore.bulkUpdate(selectedIds, {
				status: status.value
			});
			if (result.status === 'success') {
				notificationStore.pushSuccessNotification(
					'Status Updated',
					`Successfully updated ${result.data.updated} job(s) to ${status.text}`
				);
				selectedIds.forEach(id => {
					eventManager.emit('jobPositionChanged', { jobPositionId: id });
				});
				onSuccess?.();
			} else {
				notificationStore.pushErrorNotification(
					'Update Failed',
					'Failed to update jobs'
				);
			}
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update some jobs'
			);
		}
	};

	const handleBulkDelete = () => {
		dialogStore.openDialog(
			{
				title: 'Delete Jobs',
				message: `Are you sure you want to delete ${selectedIds.length} job(s)? This action cannot be undone.`,
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			async () => {
				try {
					const result = await jobPositionStore.bulkDelete(selectedIds);
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Jobs Deleted',
							`Successfully deleted ${result.data.deleted} job(s)`
						);
						setSelectedIds([]);
						onSuccess?.();
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete jobs'
						);
					}
				} catch (error) {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete some jobs'
					);
				}
			}
		);
	};

	const getBulkActions = () => {
		const priorityItems = jobPositionStore.priorityOptions.map(priority => ({
			label: (
				<div className={`job-position-priority priority-${priority.value}`}>
					{priority.text}
				</div>
			),
			onClick: () => handleBulkChangePriority(priority)
		}));

		const urgencyItems = jobPositionStore.urgencyOptions.map(urgency => ({
			label: (
				<UrgencyLabel mode="text" value={urgency.value} text={urgency.text} />
			),
			onClick: () => handleBulkChangeUrgency(urgency)
		}));

		const statusItems = jobPositionStore.statusOptions.map(status => ({
			label: <span style={{ color: status.colors.text }}>{status.text}</span>,
			onClick: () => handleBulkChangeStatus(status)
		}));

		return [
			{
				label: 'Change Priority',
				items: priorityItems
			},
			{
				label: 'Change Urgency',
				items: urgencyItems
			},
			{
				label: 'Change Status',
				items: statusItems
			},
			{
				label: 'Delete',
				isDanger: true,
				onClick: handleBulkDelete
			}
		];
	};

	const getMenuItems = row => [
		{
			label: 'Change Status',
			subItems: jobPositionStore.statusOptions
				.filter(status => status.value !== row?.status)
				.map(status => {
					return {
						...status,
						label: (
							<span style={{ color: status.colors.text }}>{status.text}</span>
						),
						onClick: async () => {
							await jobPositionStore.update(row.id, {
								job: { status: status.value }
							});
							onSuccess?.();

							eventManager.emit('jobPositionChanged', {
								jobPositionId: row.id
							});
						}
					};
				})
		},
		{
			label: 'Change Priority',
			subItems: jobPositionStore.priorityOptions
				.filter(priority => priority.value !== row?.priority)
				.map(priority => {
					return {
						...priority,
						label: (
							<div
								className={`job-position-priority priority-${priority.value}`}>
								{priority.text}
							</div>
						),
						onClick: async () => {
							await jobPositionStore.update(row.id, {
								job: { priority: priority.value }
							});
							onSuccess?.();

							eventManager.emit('jobPositionChanged', {
								jobPositionId: row.id
							});
						}
					};
				})
		},
		{
			label: 'Change Urgency',
			subItems: jobPositionStore.urgencyOptions
				.filter(urgency => urgency.value !== row?.urgency)
				.map(urgency => {
					return {
						...urgency,
						label: (
							<UrgencyLabel
								mode="text"
								value={urgency.value}
								text={urgency.text}
							/>
						),
						onClick: async () => {
							await jobPositionStore.update(row.id, {
								job: { urgency: urgency.value }
							});
							onSuccess?.();

							eventManager.emit('jobPositionChanged', {
								jobPositionId: row.id
							});
						}
					};
				})
		}
	];

	const isJobPositionProfileOpen =
		drawerStore.isOpen &&
		drawerStore.drawerId === drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE;

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th className="checkbox-column">
					<div className="checkbox-cell">
						<input
							type="checkbox"
							checked={isAllSelected}
							ref={input => {
								if (input) {
									input.indeterminate = isPartiallySelected;
								}
							}}
							onChange={handleSelectAll}
						/>
					</div>
				</th>
				{shouldShowColumn('id') && (
					<th
						style={{ width: '40px' }}
						className="is-sortable"
						onClick={() => handleHeaderClick('id')}>
						ID {isColumnSorted('id')}
					</th>
				)}
				{shouldShowColumn('priority') && (
					<th
						className="is-sortable has-text-centered"
						onClick={() => handleHeaderClick('priority')}>
						Priority {isColumnSorted('priority')}
					</th>
				)}
				{shouldShowColumn('urgency') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('urgency')}>
						Urgency {isColumnSorted('urgency')}
					</th>
				)}
				{shouldShowColumn('title') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('title')}>
						Job Title {isColumnSorted('title')}
					</th>
				)}
				{shouldShowColumn('client') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('client.name')}>
						Client {isColumnSorted('client.name')}
					</th>
				)}
				{shouldShowColumn('owner') && (
					<th className="has-text-centered">Owners</th>
				)}
				{shouldShowColumn('account_manager') && (
					<th
						className="is-sortable has-text-centered"
						onClick={() => handleHeaderClick('accountManager.name')}>
						Account Manager {isColumnSorted('accountManager.name')}
					</th>
				)}
				{shouldShowColumn('status') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('status')}>
						Status {isColumnSorted('status')}
					</th>
				)}
				{shouldShowColumn('shortlisted_candidates') && (
					<th className="has-text-centered">
						Shortlisted <br /> Candidates
					</th>
				)}
				{shouldShowColumn('role') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('role.name')}>
						Role {isColumnSorted('role.name')}
					</th>
				)}
				{shouldShowColumn('hiring_manager') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('hiringManager.name')}>
						Hiring Manager {isColumnSorted('hiringManager.name')}
					</th>
				)}
				{shouldShowColumn('total_vacancies') && (
					<th className="has-text-centered">Total Vacancies</th>
				)}
				{shouldShowColumn('created_on') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('createdAt')}>
						Created On {isColumnSorted('createdAt')}
					</th>
				)}
				{shouldShowColumn('updated_on') && (
					<th
						className="is-sortable"
						onClick={() => handleHeaderClick('updatedAt')}>
						Updated On {isColumnSorted('updatedAt')}
					</th>
				)}
				{shouldShowColumn('locations') && <th>Locations</th>}
				{shouldShowColumn('vacancies_days_open_avg') && (
					<th className="has-text-centered">Vacancies Days Open Avg</th>
				)}
				<th className="has-text-centered action-btn">
					<ColumnSelector
						target={props => (
							<button type="button" className="action-btn" {...props}>
								<img src="/ats/icons/plus-icon.svg" alt="Add button" />
							</button>
						)}
						value={visibleColumns}
						options={jobPositionStore.tableColumnsOptions}
						onChange={values => onVisibleColumnsChange(values)}
					/>
				</th>
				<th className="pending-task-column"></th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		const statusData = getStatus(row.status);
		const priorityData = getPriority(row.priority);
		const urgencyData = getUrgency(row.urgency);
		const isSelected = selectedIds.includes(row.id);

		return (
			<tr
				key={row.id}
				className={`${isJobPositionProfileOpen && String(activeRowId) === String(row.id) ? 'is-active' : ''} ${isSelected ? 'is-selected' : ''}`}
				onClick={event => {
					event.stopPropagation();

					if (!['BUTTON', 'INPUT'].includes(event.target.tagName)) {
						onClickRow?.(row);
					}
				}}>
				<td className="checkbox-column">
					<div className="checkbox-cell">
						<input
							type="checkbox"
							checked={isSelected}
							onChange={e => handleSelectRow(e, row.id)}
						/>
					</div>
				</td>
				{shouldShowColumn('id') && (
					<td className="id-column">
						<div className="id-cell">#{row.id}</div>
					</td>
				)}
				{shouldShowColumn('priority') && (
					<td className="priority-column">
						<div className="priority-cell">
							<AtsTooltip
								text={`Priority: ${priorityData.text}`}
								placement="right">
								<div
									className={`job-position-priority priority-${priorityData.value}`}>
									{priorityData.text}
								</div>
							</AtsTooltip>
						</div>
					</td>
				)}
				{shouldShowColumn('urgency') && (
					<td className="urgency-column">
						<div className="urgency-cell">
							<UrgencyLabel
								mode="text"
								value={urgencyData.value}
								text={urgencyData.text}
							/>
						</div>
					</td>
				)}
				{shouldShowColumn('title') && <td>{row.title ? row.title : ''}</td>}
				{shouldShowColumn('client') && (
					<td>{row.client?.name ? row.client?.name : ''}</td>
				)}
				{shouldShowColumn('owner') && (
					<td className="owner-column">
						<div className="owner-cell">
							{row.owners.map(({ owner }) => (
								<UserProfileAvatar
									key={owner?.id}
									imageUrl={owner?.imageUrl}
									name={owner?.name}
									size={16}
								/>
							))}
						</div>
					</td>
				)}
				{shouldShowColumn('account_manager') && (
					<td className="account-manager-column">
						<div className="account-manager-cell">
							{row.accountManager && (
								<UserProfileAvatar
									imageUrl={row.accountManager?.imageUrl}
									name={row.accountManager?.name}
									size={16}
								/>
							)}
						</div>
					</td>
				)}
				{shouldShowColumn('status') && (
					<td className="status-column">
						<div style={{ color: statusData?.colors?.text }}>
							{statusData?.text}
						</div>
					</td>
				)}
				{shouldShowColumn('shortlisted_candidates') && (
					<td className="has-text-centered">
						{row.shortlistedCandidates ?? 0}
					</td>
				)}
				{shouldShowColumn('role') && <td>{row.role?.name || ''}</td>}
				{shouldShowColumn('hiring_manager') && (
					<td>{row.hiringManager?.name || ''}</td>
				)}
				{shouldShowColumn('total_vacancies') && (
					<td className="has-text-centered">{row.totalVacancies ?? 0}</td>
				)}
				{shouldShowColumn('created_on') && (
					<td>{row.createdAt ? moment(row.createdAt).format('ll') : ''}</td>
				)}
				{shouldShowColumn('updated_on') && (
					<td>{row.updatedAt ? moment(row.updatedAt).format('ll') : ''}</td>
				)}
				{shouldShowColumn('locations') && (
					<td>
						{row.locations
							?.map(l => l.location?.name)
							.filter(Boolean)
							.join(', ') || ''}
					</td>
				)}
				{shouldShowColumn('vacancies_days_open_avg') && (
					<td className="has-text-centered">
						{row.vacanciesDaysOpenAverage ?? 0}
					</td>
				)}
				<td className="has-text-centered action-btn">
					<HoverMenu
						id={row.id}
						position="left"
						buttonIcon={
							<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
						}
						items={getMenuItems(row)}
					/>
				</td>
				<td className="pending-task-column">
					<div className="pending-task-cell">
						<PendingTaskBadge count={row.pendingTasksCount} variant="count" />
					</div>
				</td>
			</tr>
		);
	};

	return (
		<>
			<BulkActionsBar
				selectedCount={selectedIds.length}
				actions={getBulkActions()}
				onClearSelection={() => setSelectedIds([])}
			/>
			<div className={`ats-table-scroll${isEmpty ? ' is-empty-results' : ''}`}>
				<table className="table is-fullwidth">
					{renderTableHeader()}
					{loading && data.length === 0 ? (
						<AtsTableSkeleton
							columns={Math.max(visibleColumns?.length || 0, 4)}
						/>
					) : (
						!isEmpty && <tbody>{data.map(renderTableRow)}</tbody>
					)}
				</table>
				{isEmpty && <div className="job-positions-empty">{emptyState}</div>}
			</div>
		</>
	);
}

export default observer(JobPositionsTable);
